import axios from 'axios';
import { supabase } from './auth';

const appStage = process.env.REACT_APP_STAGE;
const apiBaseUrl = appStage === 'dev'
  ? process.env.REACT_APP_DEV_API_URL
  : process.env.REACT_APP_PROD_API_URL || '';

if (!apiBaseUrl) {
  // eslint-disable-next-line no-console
  console.warn('API base URL is missing. Set REACT_APP_DEV_API_URL or REACT_APP_PROD_API_URL for this build.');
}

// ---------------------------------------------------------------------------
// Token cache — keeps the request interceptor synchronous so async errors
// in supabase.auth.getSession() can never create unhandled rejections.
// ---------------------------------------------------------------------------

// Seed from localStorage so the very first request already has a token.
const seedTokenFromStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const key = Object.keys(window.localStorage)
    .find((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
  if (!key) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key));
    const session =
      parsed?.currentSession ?? (parsed?.access_token ? parsed : null);
    return session?.access_token ?? null;
  } catch {
    return null;
  }
};

let cachedToken = seedTokenFromStorage();

// Keep the cache in sync whenever auth state changes (login, logout, refresh).
if (supabase) {
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedToken = session?.access_token ?? null;
  });
}

// ---------------------------------------------------------------------------

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  withCredentials: true,
});

// Synchronous request interceptor — no async, no floating Promises.
api.interceptors.request.use((config) => {
  if (cachedToken) {
    config.headers['Authorization'] = `Bearer ${cachedToken}`;
  }
  return config;
});

// Utility to detect cancelled/aborted requests regardless of axios version.
export const isCancelError = (err) =>
  err?.name === 'AbortError' ||
  err?.name === 'CanceledError' ||
  err?.code === 'ERR_CANCELED';

// Response interceptor: suppress expected 404s for user lookups.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isCancelError(error)) return Promise.reject(error);

    const originalRequest = error.config;
    if (
      error.response?.status === 404 &&
      originalRequest?.url?.match(/^\/api\/users\/[^/]+$/)
    ) {
      return Promise.resolve({
        data: { user: null },
        status: 404,
        statusText: 'Not Found',
        headers: error.response.headers,
        config: originalRequest,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
